import React, { useState } from 'react';
import { countries as COUNTRIES } from '../data/countries';
import { getLastFetched, getRatesSource } from '../data/exchangeRates';

// Reference date for the upcoming Etsy regulatory fee changes
const JUN_22_2026 = new Date('2026-06-22T00:00:00Z');

function formatTimeUntil(target) {
  const now = new Date();
  const ms = target - now;
  if (ms <= 0) return 'today';
  const days = Math.ceil(ms / (1000 * 60 * 60 * 24));
  if (days === 1) return 'tomorrow';
  if (days < 14) return `in ${days} days`;
  const weeks = Math.round(days / 7);
  if (weeks < 9) return `in ${weeks} weeks`;
  const months = Math.round(days / 30);
  return `in about ${months} months`;
}

function formatRelativeAgo(timestamp) {
  if (!timestamp) return null;
  const ms = Date.now() - timestamp;
  const mins = Math.round(ms / 60000);
  const hrs = Math.round(ms / 3600000);
  const days = Math.round(ms / 86400000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (hrs < 24) return `${hrs}h ago`;
  return `${days}d ago`;
}

export default function UpdatesPanel({ inputs, settings, result }) {
  // Stress test slider — let users see profit impact of hypothetical fee hike
  const [stressFeeRate, setStressFeeRate] = useState(6.5);
  const [stressOffsiteRate, setStressOffsiteRate] = useState(15);

  // Compute stressed profit using simple linear adjustment from current result
  // (full recalc isn't necessary — we know the fee deltas)
  const stress = (() => {
    if (!result || result.revenue <= 0) return null;
    const sym = result.currencySymbol;
    const txDelta = (stressFeeRate - 6.5) / 100;            // e.g. +0.01 if 7.5%
    const txExtra = result.revenue * txDelta;
    const offsiteApplied = settings?.offsiteEnabled;
    const offsiteCurrentRate = (settings?.offsiteRate || 0.15);
    // eslint-disable-next-line no-unused-vars
    const offsiteDelta = offsiteApplied
      ? (stressOffsiteRate / 100 - offsiteCurrentRate)
      : 0;
    const offsiteExtra = offsiteApplied
      ? Math.min(result.revenue * (stressOffsiteRate / 100), 100) - result.offsiteFee
      : 0;
    const totalExtra = txExtra + offsiteExtra;
    const stressedProfit = result.profit - totalExtra;
    const profitDropPct = result.profit > 0 ? (totalExtra / result.profit) * 100 : 0;
    return { sym, totalExtra, stressedProfit, profitDropPct };
  })();

  // Find countries whose regulatory fee changes on Jun 22 2026
  const upcomingChanges = Object.entries(COUNTRIES)
    .filter(([, c]) =>
      typeof c.regulatoryAfterJun22_2026 === 'number'
      && c.regulatoryAfterJun22_2026 !== c.regulatory
    )
    .map(([code, c]) => ({
      code,
      name: c.name,
      flag: c.flag,
      from: c.regulatory,
      to: c.regulatoryAfterJun22_2026,
      delta: c.regulatoryAfterJun22_2026 - c.regulatory,
    }))
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));

  const lastFetched = getLastFetched();
  const source = getRatesSource();
  const sourceLabel = {
    live: '🟢 Live',
    cache: '💾 Cached',
    fallback: '📌 Default',
  }[source] || '📌 Default';

  return (
    <div className="sketch-card">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
        <h2 className="font-display text-2xl text-ink">📅 Updates</h2>
        <span className="font-body text-xs text-ink/72" style={{ marginTop: '0.5rem' }}>
          fee changes · live data status
        </span>
      </div>
      <p className="font-body text-sm text-ink/78 mb-5">
        What's changing in Etsy's fees and how this calculator stays current.
      </p>

      {/* Upcoming fee changes */}
      <section className="mb-5">
        <div className="flex items-baseline justify-between gap-2 mb-2 flex-wrap">
          <h3
            className="font-display text-sm uppercase text-ink/78"
            style={{ letterSpacing: '0.08em', fontWeight: 600 }}
          >
            Upcoming · Jun 22, 2026
          </h3>
          <span className="font-body text-xs text-amber-700" style={{ fontWeight: 600 }}>
            {formatTimeUntil(JUN_22_2026)}
          </span>
        </div>

        <p className="font-body text-sm text-ink/82 mb-3">
          Etsy is adjusting regulatory operating fees in {upcomingChanges.length} countries.
          New rates take effect automatically — your saved calculations will update.
        </p>

        <div
          style={{
            border: '1.5px solid rgba(180, 83, 9, 0.25)',
            borderRadius: '0.625rem',
            background: 'rgba(254, 243, 199, 0.35)',
            overflow: 'hidden',
          }}
        >
          {upcomingChanges.map((u, i) => {
            const fromPct = (u.from * 100).toFixed(2);
            const toPct = (u.to * 100).toFixed(2);
            const delta = ((u.to - u.from) * 100).toFixed(2);
            const isIncrease = u.delta > 0;
            return (
              <div
                key={u.code}
                className="flex items-center gap-3"
                style={{
                  padding: '0.55rem 0.85rem',
                  borderTop: i === 0 ? 'none' : '1px solid rgba(180, 83, 9, 0.15)',
                  background: i % 2 === 0 ? 'transparent' : 'rgba(254, 243, 199, 0.25)',
                }}
              >
                <span style={{ fontSize: '1.2rem', minWidth: '1.5rem' }} aria-hidden="true">
                  {u.flag}
                </span>
                <span
                  className="font-body text-sm text-ink"
                  style={{ fontWeight: 600, minWidth: '0', flex: 1 }}
                >
                  {u.name}
                </span>
                <span
                  className="font-numbers text-xs text-ink/72"
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {fromPct}%
                </span>
                <span className="text-ink/40">→</span>
                <span
                  className="font-numbers text-sm"
                  style={{
                    fontWeight: 700,
                    color: isIncrease ? '#9f1239' : '#047857',
                    fontVariantNumeric: 'tabular-nums',
                    minWidth: '3.2em',
                    textAlign: 'right',
                  }}
                >
                  {toPct}%
                </span>
                <span
                  className="font-body text-xs"
                  style={{
                    color: isIncrease ? '#9f1239' : '#047857',
                    fontWeight: 600,
                    minWidth: '3.5em',
                    textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {isIncrease ? '+' : ''}{delta}pp
                </span>
              </div>
            );
          })}
        </div>
      </section>

      {/* Stress Test — what if Etsy raises fees? */}
      {result && result.revenue > 0 && (
        <section className="mb-5">
          <h3
            className="font-display text-sm uppercase text-ink/78 mb-2"
            style={{ letterSpacing: '0.08em', fontWeight: 600 }}
          >
            Stress test · what if fees rise?
          </h3>
          <p className="font-body text-sm text-ink/82 mb-3">
            Etsy raised the transaction fee from 5% to 6.5% in 2022 with little notice.
            See how a future hike would affect your current product.
          </p>

          <div
            style={{
              padding: '0.85rem 1rem',
              background: 'rgba(159, 18, 57, 0.04)',
              border: '1.5px solid rgba(159, 18, 57, 0.2)',
              borderRadius: '0.625rem',
            }}
          >
            <div className="mb-3">
              <div className="flex justify-between mb-1">
                <label className="font-body text-xs text-ink/75" style={{ fontWeight: 600 }}>
                  Transaction fee:{' '}
                  <span className="font-numbers" style={{ fontWeight: 700, color: '#9f1239' }}>
                    {stressFeeRate.toFixed(1)}%
                  </span>
                  {stressFeeRate !== 6.5 && (
                    <span className="text-ink/68 ml-2">
                      (currently 6.5%)
                    </span>
                  )}
                </label>
              </div>
              <input
                type="range"
                min="5"
                max="10"
                step="0.5"
                value={stressFeeRate}
                onChange={(e) => setStressFeeRate(parseFloat(e.target.value))}
                style={{ width: '100%', accentColor: '#9f1239' }}
              />
              <div className="flex justify-between text-xs text-ink/68 mt-0.5" style={{ fontFamily: 'Inter, sans-serif' }}>
                <span>5%</span><span>7.5%</span><span>10%</span>
              </div>
            </div>

            {settings?.offsiteEnabled && (
              <div className="mb-3 pt-3" style={{ borderTop: '1px dashed rgba(159, 18, 57, 0.2)' }}>
                <div className="flex justify-between mb-1">
                  <label className="font-body text-xs text-ink/75" style={{ fontWeight: 600 }}>
                    Offsite Ads rate:{' '}
                    <span className="font-numbers" style={{ fontWeight: 700, color: '#9f1239' }}>
                      {stressOffsiteRate}%
                    </span>
                    {stressOffsiteRate !== 15 && (
                      <span className="text-ink/68 ml-2">(currently 15%)</span>
                    )}
                  </label>
                </div>
                <input
                  type="range"
                  min="10"
                  max="25"
                  step="1"
                  value={stressOffsiteRate}
                  onChange={(e) => setStressOffsiteRate(parseFloat(e.target.value))}
                  style={{ width: '100%', accentColor: '#9f1239' }}
                />
                <div className="flex justify-between text-xs text-ink/68 mt-0.5" style={{ fontFamily: 'Inter, sans-serif' }}>
                  <span>10%</span><span>17.5%</span><span>25%</span>
                </div>
              </div>
            )}

            {stress && (
              <div
                className="pt-3 mt-2"
                style={{ borderTop: '1px dashed rgba(159, 18, 57, 0.2)' }}
              >
                <div className="flex justify-between items-baseline">
                  <span className="font-body text-sm text-ink/75">Your profit would drop by</span>
                  <span
                    className="font-numbers"
                    style={{
                      fontSize: '1.2rem',
                      fontWeight: 700,
                      color: '#9f1239',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {stress.sym}{Math.abs(stress.totalExtra).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-baseline mt-1">
                  <span className="font-body text-sm text-ink/75">From {stress.sym}{result.profit.toFixed(2)} to</span>
                  <span
                    className="font-numbers"
                    style={{
                      fontSize: '1.2rem',
                      fontWeight: 700,
                      color: stress.stressedProfit >= 0 ? '#047857' : '#9f1239',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {stress.sym}{stress.stressedProfit.toFixed(2)}
                  </span>
                </div>
                {stress.profitDropPct > 0 && (
                  <p className="font-body text-xs text-ink/72 mt-2" style={{ lineHeight: 1.5 }}>
                    {' '}~ that's a{' '}
                    <strong style={{ color: '#9f1239' }}>{stress.profitDropPct.toFixed(0)}% cut</strong>
                    {' '}to your profit.{stress.stressedProfit < 0 ? ' This product would lose money.' : ''} ~
                  </p>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Data status */}
      <section className="mb-3">
        <h3
          className="font-display text-sm uppercase text-ink/78 mb-2"
          style={{ letterSpacing: '0.08em', fontWeight: 600 }}
        >
          Data status
        </h3>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          <li className="flex items-center justify-between py-2" style={{ borderBottom: '1px solid rgba(26,26,46,0.06)' }}>
            <span className="font-body text-sm text-ink">Exchange rates</span>
            <span className="font-body text-xs text-ink/78">
              {sourceLabel}
              {lastFetched && (
                <span className="text-ink/78"> · {formatRelativeAgo(lastFetched)}</span>
              )}
            </span>
          </li>
          <li className="flex items-center justify-between py-2" style={{ borderBottom: '1px solid rgba(26,26,46,0.06)' }}>
            <span className="font-body text-sm text-ink">Country fee schedules</span>
            <span className="font-body text-xs text-ink/78">2025–2026 Etsy data</span>
          </li>
          <li className="flex items-center justify-between py-2">
            <span className="font-body text-sm text-ink">Total countries supported</span>
            <span className="font-body text-xs text-ink/78 font-numbers">
              {Object.keys(COUNTRIES).length}
            </span>
          </li>
        </ul>
      </section>

      <p className="font-note text-xs text-ink/78 mt-2 leading-tight">
        ~ Etsy occasionally changes fees with little notice; check{' '}
        <a
          href="https://help.etsy.com/hc/en-us/articles/115014483627"
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'underline', color: 'inherit' }}
        >
          their fee schedule
        </a>
        {' '}for the latest official rates ~
      </p>
    </div>
  );
}
